/** @module api/users */

import { apiCall } from '@/api/index'
import { addQueryParamsToReqPayload } from '@/api/configure'
import {
  apiV1CertifiedPartnerIdentificationPath,
  apiV1CertifiedPartnerIdentificationsPath,
  apiV1UsersMeRelationshipCertifiedPartnerIdentificationPath,
  addRolesApiV1UserPath,
  addToGroupsApiV1UserPath,
  showApiV1CertifiedPartnerIdentificationPath,
  apiV1UserPath,
  apiV1UsersPath,
  certifiedPartnerGroupMatchesApiV1UserPath,
  certifiedPartnerVerificationConfirmationApiV1UserPath,
  relationshipEmssApiV1UserPath,
  relationshipGroupsApiV1UserPath,
  removeFromGroupsApiV1UserPath,
  removeRolesApiV1UserPath,
  showPermissionEmsApiV1UserPath,
  showPermissionGroupApiV1UserPath,
  updatePermissionGroupApiV1UserPath
} from '@/../lib/routes'
import { ROLE_TO_CTXT_MAP } from '@/store/modules/users'

/**
 * API GET to get a collection of User(s)
 *
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection, e.g. `email, roles`
 * @param {object} params.filter (optional) is a filter to by applied to the resource query
 * @param {string} params.sort (optional) is a sort-string (resource attribute/field name) the returned collection should be sorted by.
 *
 * @return {promise}
 */
export function getUsers({ ctxt = 'admin', fields = [], filter = {}, sort = '' } = {}) {
  const payload = {}
  addQueryParamsToReqPayload(payload, 'users', { fields, filter, sort })

  return apiCall({
    path: apiV1UsersPath(ctxt),
    payload
  })
}

/**
 * API GET to get a User
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.fields (optional) is a list of resource fields (attributes) to be included in the returned collection
 *
 * @return {promise}
 */
export function getUser({ id, ctxt = 'admin', fields = [] }) {
  const payload = {}
  addQueryParamsToReqPayload(payload, 'users', { fields })

  return apiCall({
    path: apiV1UserPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get all EMSs for a specific User.
 *
 * The set of returned EMSs is intersected with the set of EMSs,
 * which can be accessed by the current-user, performing the request.
 *
 * Note: by json:api specification, only EMS-ID and type is returned.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID for whom the EMSs are requested
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 * @param {object} params.membership (optional) supports to specify the membership, which builds the User .-< EMSs relation. Supported are `'Groups::OrganizationGroup'`, `'Groups::EmsAccessGroup'`, and `''`. If empty, all EMSs are returned.
 *
 * @return {promise}
 */
export function getUserRelationshipEmss({ id, ctxt = 'admin', membership = 'Groups::OrganizationGroup' }) {
  const payload = {}
  const filter = {
    type: membership
  }
  addQueryParamsToReqPayload(payload, 'groups', { filter })

  return apiCall({
    path: relationshipEmssApiV1UserPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get all OrganizationGroup(s) for a specific User.
 *
 * The set of returned Groups is intersected with the set of Groups,
 * which can be accessed by the current-user, performing the request.
 *
 * Note: by json:api specification, only EMS-ID and type is returned.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID for whom the Groups are requested
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 * @param {object} params.membership (optional) supports to specify the membership, which builds the User .-< EMSs relation. Supported are `'Groups::OrganizationGroup'`, `'Groups::EmsAccessGroup'`, and `''`. If empty, all EMSs are returned.
 *
 * @return {promise}
 */
export function getUserRelationshipGroups({ id, ctxt = 'admin', membership = 'Groups::OrganizationGroup' }) {
  const payload = {}
  const filter = {
    type: membership
  }
  addQueryParamsToReqPayload(payload, 'groups', { filter })

  return apiCall({
    path: relationshipGroupsApiV1UserPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get the user's EMS "max-permission" for a specific User and EMS.
 * The "max-permission" is obtained by aggregation (bitwise or) of all user's group-permissions for the specified EMS.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID for whom the permissions are requested
 * @param {integer|string} params.emsId has to be the ems's ID for whom the permissions are requested
 * @param {string} params.ctxt is the context of the request
 * @param {object} params.membership (optional) supports to specify the membership, which builds the User .-. EMS relation. Supported are `'Groups::OrganizationGroup'`, `'Groups::EmsAccessGroup'`, and `''`. If empty, all EMSs are returned.
 *
 * @return {promise}
 */
export function getUserPermissionEms({ id, emsId, ctxt = 'admin', membership = 'Groups::OrganizationGroup' }) {
  const payload = {}
  const filter = {
    type: membership
  }
  addQueryParamsToReqPayload(payload, 'groups', { filter })

  return apiCall({
    path: showPermissionEmsApiV1UserPath(ctxt, id, emsId),
    payload
  })
}

/**
 * API GET to get all Group permissions for a specific User and Group.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID for whom the permissions are requested
 * @param {integer|string} params.groupID has to be the group's ID for whom the permissions are requested
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 * @param {object} params.membership (optional) supports to specify the membership, which builds the User .-< EMSs relation. Supported are `'Groups::OrganizationGroup'`, `'Groups::EmsAccessGroup'`, and `''`. If empty, all EMSs are returned.
 *
 * @return {promise}
 */
export function getUserPermissionGroup({
  id,
  groupId,
  ctxt = 'admin',
  membership = 'Groups::OrganizationGroup',
  mode = null
}) {
  const payload = {
    mode
  }
  const filter = {
    type: membership
  }
  addQueryParamsToReqPayload(payload, 'groups', { filter })

  return apiCall({
    path: showPermissionGroupApiV1UserPath(ctxt, id, groupId),
    payload
  })
}

/**
 * API POST to add roles for a specific.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID for whom the roles are added
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 * @param Array{string} params.roles is an Array of possible role names ['customer', 'technician', 'operator', 'admin', 'demo', 'mydemo']
 *
 * @return {promise}
 */
export function addUserRoles({ id, ctxt = 'admin', roles = [] }) {
  const payload = {
    data: []
  }
  for (const role of roles) {
    payload.data.push({
      type: 'roles',
      id: role,
      name: role
    })
  }
  return apiCall({
    method: 'POST',
    path: addRolesApiV1UserPath(ctxt, id),
    payload
  })
}

/**
 * API DELETE to remove roles for a specific.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID for whom the roles are added
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 * @param Array{string} params.roles is an Array of possible role names ['customer', 'technician', 'operator', 'admin', 'demo', 'mydemo']
 *
 * @return {promise}
 */
export function removeUserRoles({ id, ctxt = 'admin', roles = [] }) {
  const payload = {
    data: []
  }
  for (const role of roles) {
    payload.data.push({
      type: 'roles',
      id: role,
      name: role
    })
  }
  return apiCall({
    method: 'DELETE',
    path: removeRolesApiV1UserPath(ctxt, id),
    payload
  })
}

/**
 * API PATCH to set user permissions for a group.
 *
 * Note:
 *   The BE will NOT merge (by default) exising permission flag-values with new ones. (Otherwise removing a flag would not be possible.)
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID for whom the roles are added
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 * @param {object} params.perms is the permission to be set.
 * @param {object} params.preservePerms is the permission to be preserved (optional). If present, the provided permission-flag for each role (context) will NOT be overwritten by the BE.
 * @param {boolean} params.promoteUser allows to optionally promote a user to be non-(demo + mydemo) during permission set. This is required to remove the (default) demo + mydemo roles from a newly signed-up user.
 *
 * @return {promise}
 */
export function updateUserGroupPermission({
  id,
  groupId,
  ctxt = 'admin',
  perms = {},
  preservePerms = {},
  promoteUser = false
}) {
  const permission = {}
  const preservePermission = {}

  for (const role in perms) {
    const ctxt = ROLE_TO_CTXT_MAP[role]
    if (!ctxt) {
      throw new TypeError(`Permission object contains unsupported role ${role}.`)
    }
    permission[ctxt] = perms[role]
    if (preservePerms[role]) {
      preservePermission[ctxt] = preservePerms[role]
    }
  }

  const payload = {
    data: {
      type: 'user_group_membership',
      id,
      attributes: {
        permission,
        preservePermission,
        promoteUser
      }
    }
  }

  return apiCall({
    method: 'PATCH',
    path: updatePermissionGroupApiV1UserPath(ctxt, id, groupId),
    payload
  })
}

/**
 * API POST to add a user to a group.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID
 * @param Array{integer|string} params.groupIds has to be the group's IDs
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 *
 * @return {promise}
 */
export function addUserToGroups({ ctxt = 'admin', id, groupIds }) {
  const payload = {
    data: []
  }

  for (const groupId of groupIds) {
    if (groupId) {
      payload.data.push({
        type: 'groups',
        id: groupId
      })
    }
  }

  return apiCall({
    method: 'POST',
    path: addToGroupsApiV1UserPath(ctxt, id),
    payload
  })
}

/**
 * API DELETE to remove a user from a group.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID
 * @param Array{integer|string} params.groupIds has to be the group's IDs
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 *
 * @return {promise}
 */
export function removeUserFromGroups({ id, groupIds, ctxt = 'admin' }) {
  const payload = {
    data: []
  }

  for (const groupId of groupIds) {
    if (groupId) {
      payload.data.push({
        type: 'groups',
        id: groupId
      })
    }
  }

  return apiCall({
    method: 'DELETE',
    path: removeFromGroupsApiV1UserPath(ctxt, id),
    payload
  })
}

/**
 * API DELETE to delete a user.
 *
 * @function
 *
 * @param {object} params
 * @param {integer|string} params.id has to be the user's ID
 * @param {string} params.ctxt is the context of the request (Currently only `admin` context is authorized by BE.)
 *
 * @return {promise}
 */
export function deleteUser({ id, ctxt = 'admin' }) {
  const payload = {
    data: {
      type: 'users',
      id
    }
  }

  return apiCall({
    method: 'DELETE',
    path: apiV1UserPath(ctxt, id),
    payload
  })
}

/**
 * API GET to get the Certified Partner Identification
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.id is the certified partner identification ID
 *
 * @return {promise}
 */
export function getCertifiedPartnerIdentification({ ctxt = 'admin', id } = {}) {
  return apiCall({
    path: showApiV1CertifiedPartnerIdentificationPath(ctxt, id)
  })
}

export function getCurrentUserRelationshipCertifiedPartnerIdentification() {
  return apiCall({
    path: apiV1UsersMeRelationshipCertifiedPartnerIdentificationPath()
  })
}

export function getCurrentUserCertifiedPartnerIdentification({ id } = {}) {
  return apiCall({
    path: apiV1CertifiedPartnerIdentificationPath(id)
  })
}

export function createCurrentUserCertifiedPartnerIdentification({
  companyName,
  companyRegistrationNumber,
  energyManagerSerialnos
} = {}) {
  const payload = {
    data: {
      type: 'certified_partner_identifications',
      attributes: {
        companyName,
        companyRegistrationNumber,
        energyManagerSerialnos
      }
    }
  }

  return apiCall({
    method: 'POST',
    path: apiV1CertifiedPartnerIdentificationsPath(),
    payload
  })
}

export function updateCurrentUserCertifiedPartnerIdentification({
  id,
  companyName,
  companyRegistrationNumber,
  energyManagerSerialnos
} = {}) {
  const payload = {
    data: {
      type: 'certified_partner_identifications',
      id,
      attributes: {
        companyName,
        companyRegistrationNumber,
        energyManagerSerialnos
      }
    }
  }

  return apiCall({
    method: 'PATCH',
    path: apiV1CertifiedPartnerIdentificationPath(id),
    payload
  })
}

export function deleteCurrentUserCertifiedPartnerIdentification({ id } = {}) {
  return apiCall({
    method: 'DELETE',
    path: apiV1CertifiedPartnerIdentificationPath(id)
  })
}

/**
 * API GET to get the Certified Partner Group Matches
 * @function
 *
 * @param {object} params
 * @param {string} params.ctxt is the context of the request
 * @param {string} params.userId
 * @param {string} params.limit limits the number of matches returned
 *
 * @return {promise}
 */
export function fetchCertifiedPartnerGroupMatches({ ctxt = 'admin', userId, limit = 10 }) {
  const payload = {}
  if (limit)
    payload.filter = {
      limit
    }

  return apiCall({
    path: certifiedPartnerGroupMatchesApiV1UserPath(ctxt, userId),
    payload
  })
}

export function sendCertifiedPartnerVerificationConfirmation({ ctxt = 'admin', userId }) {
  return apiCall({
    method: 'POST',
    path: certifiedPartnerVerificationConfirmationApiV1UserPath(ctxt, userId)
  })
}
